// The notification centre is not a separate list of its own: every notification comes from something the app already
// knows (an approval waiting, an invoice overdue, something Claude flagged, a reminder due soon, a shipment update).
import { jordanDate } from '@serb/shared';
import { addDays, daysBetween, shortDate } from '../../lib/dates';
import { daysSince } from '../../lib/rules';
import { getSettings } from '../../lib/settingsStore';
import type { Task } from '@serb/shared';
import type { Approval } from '../approvals/sample';
import { chaseOf } from '../shipments/sample';
import type { Shipment } from '../shipments/sample';
import { whenOf, type LogEntry } from '../activity/sample';
import type { Fin } from '../finance/calc';
import { statusOf, type ReminderItem } from '../reminders/model';

export type NotifGroup = 'Needs Attention' | 'Updates' | 'Reminders';
export type NotifCategory =
  'Approvals' | 'Shipment Updates' | 'Financial Alerts' | 'Reminders' | 'Claude Errors' | 'Routine Updates';
export type Priority = 'High' | 'Normal';

export type Notif = {
  id: string;
  group: NotifGroup;
  category: NotifCategory;
  srb: string;
  title: string;
  text: string;
  by: 'Claude' | 'System' | 'You';
  /** when it happened or is due, as words */
  when: string;
  priority: Priority;
  action: { label: string; to: string };
};

/** The switch in Settings that controls each kind */
const SWITCH: Record<NotifCategory, keyof ReturnType<typeof getSettings>['notifications']> = {
  Approvals: 'approvals',
  'Shipment Updates': 'shipmentUpdates',
  'Financial Alerts': 'financialAlerts',
  Reminders: 'reminders',
  'Claude Errors': 'claudeErrors',
  'Routine Updates': 'routineUpdates',
};

const stamp = whenOf;
const byOf = (e: LogEntry): Notif['by'] => (e.by === 'You' ? 'You' : e.type === 'System' ? 'System' : 'Claude');

/** Everything that wants Ibraheem's attention or knowledge, newest first, minus the kinds he switched off */
export type NotifSources = {
  reminders: ReminderItem[];
  fins: Fin[];
  approvals: Approval[];
  log: LogEntry[];
  tasks: Task[];
  ships: Shipment[];
};

export function buildNotifications({ reminders, fins, approvals, log, tasks, ships }: NotifSources): Notif[] {
  const today = jordanDate();
  const out: Notif[] = [];

  // approvals he has not decided yet (the urgent ones)
  approvals
    .filter((a) => a.status === 'pending' && a.urgency !== 'Low' && a.urgency !== 'Normal')
    .forEach((a) =>
      out.push({
        id: `appr-${a.id}`,
        group: 'Needs Attention',
        category: 'Approvals',
        srb: a.srb,
        title: `${a.title} awaiting approval`,
        text: `Claude prepared it: ${a.summary}`,
        by: 'Claude',
        when: a.age,
        priority: a.urgency === 'High' ? 'High' : 'Normal',
        action: {
          label: a.kind.includes('message') || a.kind === 'agent-message' ? 'Review Message' : 'Review Approval',
          to: `/approvals/${a.id}`,
        },
      }),
    );
  {
    // what Claude flagged, and what is just news
    log.forEach((e) => {
      if (e.status === 'Needs Attention')
        out.push({
          id: `log-${e.id}`,
          group: 'Needs Attention',
          category: 'Financial Alerts',
          srb: e.srb,
          title: e.activity === 'Checked agent invoice' ? 'Missing agent invoice information' : e.activity,
          text: `Claude: ${e.details}`,
          by: 'Claude',
          when: stamp(e),
          priority: 'High',
          action: { label: 'Open Shipment', to: `/shipments/${e.srb}` },
        });
      else if (e.type === 'Shipment' && e.status === 'Completed' && daysSince(e.date) <= 3)
        out.push({
          id: `log-${e.id}`,
          group: 'Updates',
          category: 'Shipment Updates',
          srb: e.srb,
          title: e.activity,
          text: e.details,
          by: byOf(e),
          when: stamp(e),
          priority: e.activity.includes('schedule') ? 'High' : 'Normal',
          action: { label: 'View Shipment', to: `/shipments/${e.srb}` },
        });
      else if (e.type === 'System')
        out.push({
          id: `log-${e.id}`,
          group: 'Updates',
          category: 'Routine Updates',
          srb: e.srb,
          title: e.activity,
          text: e.details,
          by: 'System',
          when: stamp(e),
          priority: 'Normal',
          action: { label: 'Open Activity', to: `/history/${e.id}` },
        });
    });
    // overdue customer invoices
    fins
      .filter((f) => f.overdue)
      .forEach((f) =>
        out.push({
          id: `inv-${f.r.invoiceNo}`,
          group: 'Needs Attention',
          category: 'Financial Alerts',
          srb: f.r.srb,
          title: 'Customer invoice overdue',
          text: `Invoice ${f.r.invoiceNo} is ${Math.abs(f.r.dueInDays)} days overdue.`,
          by: 'System',
          when: shortDate(today),
          priority: 'High',
          action: { label: 'View Invoice', to: `/shipments/${f.r.srb}` },
        }),
      );
  }

  // jobs Claude could not do
  tasks
    .filter((t) => t.status === 'failed')
    .forEach((t) =>
      out.push({
        id: `task-${t.id}`,
        group: 'Needs Attention',
        category: 'Claude Errors',
        srb: t.code,
        title: 'Claude could not finish a task',
        text: `${t.task}${t.result ? `: ${t.result}` : ''}`,
        by: 'Claude',
        when: shortDate(jordanDate(new Date(t.updatedAt))),
        priority: 'High',
        action: { label: "Open Claude's Tasks", to: '/reminders' },
      }),
    );

  // shipments that are late or waiting too long for an answer
  ships.forEach((s) => {
    const chase = chaseOf(s);
    if (s.stage !== 'Delayed' && !chase) return;
    out.push({
      id: `ship-${s.srb}`,
      group: 'Needs Attention',
      category: 'Shipment Updates',
      srb: s.srb,
      title: s.stage === 'Delayed' ? 'Shipment delayed' : `Chase the ${chase!.who}`,
      text:
        s.stage === 'Delayed'
          ? `${s.customer}: ${s.next.text || 'check the new schedule'}`
          : `${s.customer}: no answer for ${chase!.days} days.`,
      by: 'System',
      when: s.updated,
      priority: s.stage === 'Delayed' ? 'High' : 'Normal',
      action: { label: 'Open Shipment', to: `/shipments/${s.srb}` },
    });
  });

  // reminders due today or in the next few days (or overdue)
  reminders
    .filter((r) => !r.complete && daysBetween(today, r.date) <= 3)
    .forEach((r) => {
      const s = statusOf(r, today);
      out.push({
        id: `rem-${r.id}`,
        group: 'Reminders',
        category: 'Reminders',
        srb: r.srb,
        title: r.title,
        text: `${s.label === 'Overdue' ? 'Overdue' : s.label === 'Today' ? 'Due today' : s.label}, ${shortDate(r.date)}${r.time ? ' ' + r.time : ''}`,
        by: 'System',
        when: shortDate(r.date),
        priority: daysBetween(today, r.date) <= 0 ? 'High' : 'Normal',
        action: { label: 'View Reminder', to: '/reminders' },
      });
    });

  const on = getSettings().notifications;
  return out.filter((n) => on[SWITCH[n.category]]);
}

/** The reminders in the next 7 days, for the deadlines box */
export const nextWeek = (reminders: ReminderItem[]) => {
  const today = jordanDate();
  return reminders.filter((r) => !r.complete && r.date >= today && r.date <= addDays(today, 7));
};
